import { last, orderBy, sortBy, sumBy } from "lodash";
import moment from "moment";
import { useState } from "react";
import { RiTeamLine } from "react-icons/ri";
import {
  GoalOverviewRelatedEntityGoalFragmentFragment,
  TeamNode,
  UserNode,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSortDir } from "@components/table/table-constants";
import Tooltip from "@components/tooltip/tooltip";
import { removeCurrentYear } from "@helpers/helpers";

const getMostRecentUpdate = (
  goals: GoalOverviewRelatedEntityGoalFragmentFragment[],
) => {
  const timestamps = goals.reduce((memo, goal) => {
    if (goal.activities.edges?.[0]?.node?.created) {
      return [...memo, goal.activities.edges[0].node.created];
    }
    return memo;
  }, [] as string[]);
  if (timestamps.length === 0) {
    return null;
  }
  return last(sortBy(timestamps));
};

const getOverallProgress = (
  goals: GoalOverviewRelatedEntityGoalFragmentFragment[],
) => {
  if (goals.length === 0) {
    return null;
  }
  const sum = sumBy(goals, "progress");
  if (sum === 0) {
    return 0;
  }
  return Math.floor(sum / goals.length);
};

enum SortKeyEnum {
  label = "label",
  lastUpdate = "lastUpdate",
  avgProgress = "avgProgress",
}

export type GoalOverviewUserOrTeamType =
  | Pick<UserNode, "__typename" | "id" | "name">
  | Pick<TeamNode, "__typename" | "id" | "title">;

const GoalOverviewRelatedEntitiesTableRow = ({
  entity,
}: {
  entity: GoalOverviewUserOrTeamType & {
    goals: GoalOverviewRelatedEntityGoalFragmentFragment[];
  };
}) => {
  const label = useLabel();
  const maxGoalsToShow = 10;
  const [showAllGoals, setShowAllGoals] = useState(false);

  return (
    <TableBodyRow key={entity.id}>
      <TableBodyCell>
        {entity.__typename === "UserNode" ? (
          <div className="text-gray-800 flex items-center gap-1.5">
            <Avatar user={entity} size={5} />
            <AppLink
              to={`/goals/user/${entity.id}`}
              className="hover:underline"
            >
              {entity.name}
            </AppLink>
          </div>
        ) : entity.__typename === "TeamNode" ? (
          <div className="text-gray-800 flex items-center gap-1.5">
            <RiTeamLine className="w-5 h-5 text-gray-500" />
            <AppLink
              to={`/goals/team/${entity.id}`}
              className="hover:underline"
            >
              {entity.title}
            </AppLink>
          </div>
        ) : null}
      </TableBodyCell>
      <TableBodyCell>
        <div className="flex items-center gap-1">
          {entity.goals.length > 0
            ? `${getOverallProgress(entity.goals)}%`
            : null}
        </div>
      </TableBodyCell>
      <TableBodyCell>
        <div className="flex items-center gap-0.5">
          {entity.goals.slice(0, maxGoalsToShow).map((goal) => (
            <GoalProgress
              withPopover={true}
              key={goal.id}
              artifact={goal}
              goalProgressView={GoalProgressView.pillOnly}
            />
          ))}
          {entity.goals.length > maxGoalsToShow && (
            <>
              {showAllGoals && (
                <Modal onClose={() => setShowAllGoals(false)}>
                  <div className="flex flex-col gap-6 p-6">
                    <ModalTitle onClose={() => setShowAllGoals(false)}>
                      {entity.goals.length}{" "}
                      {label("goal", {
                        pluralize: entity.goals.length,
                        capitalize: true,
                      })}
                    </ModalTitle>
                    <div className="flex flex-col divide-y divide-gray-100">
                      {entity.goals.map((goal) => (
                        <div className="py-2" key={goal.id}>
                          <Artifact artifact={goal} />
                        </div>
                      ))}
                    </div>
                  </div>
                </Modal>
              )}
              <Button
                theme={buttonTheme.text}
                size={ButtonSize.small}
                text={`+${entity.goals.length - maxGoalsToShow}`}
                onClick={() => setShowAllGoals(true)}
              />
            </>
          )}
        </div>
      </TableBodyCell>
      <TableBodyCell>
        <div className="flex items-center gap-1">
          {getMostRecentUpdate(entity.goals) ? (
            <Tooltip
              text={moment(getMostRecentUpdate(entity.goals)).format("LLLL")}
            >
              <span>
                {removeCurrentYear(
                  moment(getMostRecentUpdate(entity.goals)).format("ll"),
                )}
              </span>
            </Tooltip>
          ) : (
            "No updates."
          )}
        </div>
      </TableBodyCell>
    </TableBodyRow>
  );
};

const GoalOverviewRelatedEntitiesTable = ({
  entities,
  entityLabel,
}: {
  entities: (GoalOverviewUserOrTeamType & {
    goals: GoalOverviewRelatedEntityGoalFragmentFragment[];
  })[];
  entityLabel: string;
}) => {
  const [sortKey, setSortKey] = useState<SortKeyEnum>(SortKeyEnum.label);
  const [sortDir, setSortDir] = useState<TableSortDir>("asc");
  const label = useLabel();

  const sortedEntities = orderBy(
    entities,
    (entity) => {
      if (sortKey === SortKeyEnum.label && entity.__typename === "UserNode")
        return [entity.name];
      if (sortKey === SortKeyEnum.label && entity.__typename === "TeamNode")
        return [entity.title];
      if (sortKey === SortKeyEnum.lastUpdate)
        return [getMostRecentUpdate(entity.goals)];
      if (sortKey === SortKeyEnum.avgProgress)
        return [getOverallProgress(entity.goals)];
    },
    [sortDir],
  );

  const handleClickSorting = (newSortKey: SortKeyEnum) => () => {
    if (newSortKey !== sortKey) {
      setSortKey(newSortKey);
    } else {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    }
  };

  return (
    <TableContainer className="shadow-sm border-0">
      <Table>
        <TableHeadRow>
          <TableHeadCell
            sorted={sortKey === SortKeyEnum.label ? sortDir : undefined}
            onClick={handleClickSorting(SortKeyEnum.label)}
          >
            Name
          </TableHeadCell>
          <TableHeadCell
            width="42"
            sorted={sortKey === SortKeyEnum.avgProgress ? sortDir : undefined}
            onClick={handleClickSorting(SortKeyEnum.avgProgress)}
          >
            Avg progress
          </TableHeadCell>
          <TableHeadCell width="56">
            {label("goal", { capitalize: true, pluralize: true })}
          </TableHeadCell>
          <TableHeadCell
            width="48"
            sorted={sortKey === SortKeyEnum.lastUpdate ? sortDir : undefined}
            onClick={handleClickSorting(SortKeyEnum.lastUpdate)}
          >
            Last update
          </TableHeadCell>
        </TableHeadRow>
        <TableBody>
          {sortedEntities.map((entity) => (
            <GoalOverviewRelatedEntitiesTableRow
              key={entity.id}
              entity={entity}
            />
          ))}
          {sortedEntities.length === 0 && (
            <TableBodyRow>
              <TableBodyCell colSpan={4}>
                No {label(entityLabel, { pluralize: true })}.
              </TableBodyCell>
            </TableBodyRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GoalOverviewRelatedEntitiesTable;
